import { isValid } from 'date-fns';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { useWatch } from 'react-hook-form';
import {
  amountField,
  booleanField,
  simpleMultilineTextField,
  useFundIdField,
  useGetCurrencyIdField,
} from '../../../data-fields/CommonFields';
import { transactionDateField } from '../../../data-fields/TransactionFields';
import { RendererType } from '../../../data-models/field-definition';
import { INumberMeta, ISelectMeta } from '../../../data-models/field3.data-model';
import { MetricsTransactionDataModel } from '../../../schemas/MetricsTransaction.schema';
import { fundsByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { companyMetricsAtomJ } from '../../../services/state/CompanyMetricsStateJ';
import { FMT } from '../../../util/formatter-service';
import { PpsFormatterDataModel } from '../../../util/formatters/NumericFormatters';
import { createFormField, IFormField } from '../../../view-models/form.view-model';
import {
  ITransactionViewModel,
  restructureKeyToFieldKey,
} from '../../../view-models/transaction-form.view-model';

const ValuationImpactOption = {
  amount: 'Change in Unrealized',
  pps: 'PPS',
};

export function useInternalValuationFields(companyId: number) {
  const metrics = useAtomValue(companyMetricsAtomJ({ companyId }));
  const fundsMap = useAtomValue(fundsByIdMapAtom);
  const getCurrencyIdField = useGetCurrencyIdField();
  const fundIdField = useFundIdField();
  const transactionDate = useWatch<ITransactionViewModel>({ name: 'transactionDate' });
  const showPpsShareClass = useWatch<ITransactionViewModel>({ name: '_viewModel.showPpsShareClass' });
  const valuationImpact = useWatch<ITransactionViewModel>({ name: 'valuationImpact' });

  return useCallback(() => {
    const fields: IFormField<unknown>[] = [];

    fields.push(transactionDateField(), getCurrencyIdField('currencyid', 'Currency', true));

    fields.push(
      createFormField<ISelectMeta<boolean>>({
        key: 'valuationImpact',
        label: 'PPS/Change in Unrealized',
        required: true,
        renderer: RendererType.singleSelect,
        rendererMeta: {
          values: [
            { value: true, displayName: ValuationImpactOption.pps },
            { value: false, displayName: ValuationImpactOption.amount },
          ],
        },
        disableClearable: true,
      })
    );

    if (valuationImpact) {
      fields.push(
        booleanField({
          key: '_viewModel.showPpsShareClass',
          label: 'Differentiate PPS by share class',
          required: false,
        })
      );

      if (showPpsShareClass) {
        const latestRestructureTransactions = getAllRestructureIdsMeta(
          metrics?.metrics[0]?.transactions ?? [],
          new Date(transactionDate as string)
        );
        const restructureFields = Array.from(latestRestructureTransactions.values()).map((transaction) => {
          const { fundId, position, restructureId, transactionDate } = transaction;
          const label = position ?? FMT.format('dateNumeric', transactionDate);
          const fundLabel =
            fundId && fundsMap.get(fundId)?.name ? `Fund: ${fundsMap.get(fundId)?.name}, ` : '';
          const description = `${fundLabel}Acquired Date: ${FMT.format('dateNumeric', transactionDate)}`;

          return createFormField<INumberMeta>({
            key: `_viewModel.ppsShareClass.${restructureKeyToFieldKey(restructureId ?? '')}`,
            label,
            description,
            required: true,
            renderer: RendererType.number,
            formatter: PpsFormatterDataModel,
          });
        });

        fields.push(...restructureFields);
      } else {
        fields.push(
          amountField({
            key: 'pricePerShare',
            label: 'Price Per Share',
            required: true,
          })
        );
      }
    } else {
      fields.push(fundIdField);
      fields.push(
        amountField({
          key: 'valuationAmount',
          label: 'Change in Unrealized',
          required: true,
        })
      );
    }

    fields.push(simpleMultilineTextField({ key: 'notes', label: 'Notes' }));

    return fields;
  }, [
    getCurrencyIdField,
    valuationImpact,
    fundIdField,
    showPpsShareClass,
    metrics?.metrics,
    transactionDate,
    fundsMap,
  ]);
}

export type IRestructureMeta = Pick<
  MetricsTransactionDataModel,
  'transactionDate' | 'fundId' | 'position'
> & {
  restructureId: string;
};

/**
 * Return all restructure ids regardless of fund.
 */
export function getAllRestructureIdsMeta(
  transactions: MetricsTransactionDataModel[],
  newTransactionDate: Date
) {
  const res = new Map<string, IRestructureMeta>();
  const quantitySumsByRestructureId = new Map<string, number>();

  if (transactions.length === 0 || !isValid(newTransactionDate)) {
    return res;
  }

  transactions.forEach((transaction) => {
    const { fundId, position, quantity, restructureId, transactionDate: transactionDateISO } = transaction;
    const transactionDate = new Date(transactionDateISO);
    const isBeforeNewTransactionDate = transactionDate.getTime() < newTransactionDate.getTime();

    if (restructureId && isBeforeNewTransactionDate) {
      const currentLatestTransaction = res.get(restructureId);
      const isLatestTransaction =
        currentLatestTransaction === undefined ||
        (transactionDate.getTime() >= new Date(currentLatestTransaction.transactionDate).getTime() &&
          quantity > 0);

      if (isLatestTransaction) {
        res.set(restructureId, {
          transactionDate: transactionDateISO,
          position: position ?? currentLatestTransaction?.position,
          fundId: fundId ?? currentLatestTransaction?.fundId,
          restructureId,
        });
      }

      quantitySumsByRestructureId.set(
        restructureId,
        (quantitySumsByRestructureId.get(restructureId) ?? 0) + quantity
      );
    }
  });

  for (const meta of res.values()) {
    if (quantitySumsByRestructureId.get(meta.restructureId)! <= 0) {
      res.delete(meta.restructureId);
    }
  }

  return res;
}
